// 2026-09-09 - FormuLearning - 전역 알림(토스트) — 이슈 #72 (M901-05)
// 파일 위치: frontend/src/components/feedback/Toast.tsx
//
// 왜 필요한가: 프로젝트 로드가 **성공했는지 취소됐는지**를 알리는 자리가 없었다.
//   좌측 패널에서 프로젝트를 클릭해도 곧바로 열리지 않고 Load 모달이 뜨며,
//   `Load Project` 를 눌러야 전환된다. Cancel·닫기·backdrop 으로 빠져나오면
//   **화면은 이전 프로젝트 그대로**인데 모달에 다른 프로젝트 이름이 크게 떴다
//   사라지므로, 그쪽으로 갔다고 믿기 쉽다. 그 착각 위에서 Step 1 을 재검색하면
//   엉뚱한 프로젝트가 덮인다(260901 회의 신고 건의 실제 형상).
//
// 어디에도 안 뜨는 «아무 일도 일어나지 않았다»를 말해 주는 것이 이 컴포넌트의 일이다.
//   `window.alert` 을 쓰지 않는다 — 확인 클릭을 요구할 일이 아니고 연속 작업을 끊는다.

import { useEffect } from 'react'
import { useToastStore, type Toast as ToastItem } from '../../stores/toastStore'

const TONE: Record<ToastItem['tone'], { color: string; bg: string; mark: string }> = {
  info: { color: 'var(--color-accent-teal)', bg: 'var(--bg-chip-teal)', mark: '✓' },
  warn: { color: 'var(--color-warn)', bg: 'color-mix(in srgb, var(--color-warn) 14%, transparent)', mark: '!' },
}

function ToastRow({ item }: { item: ToastItem }) {
  const dismiss = useToastStore((s) => s.dismiss)
  const t = TONE[item.tone]

  // 자동 소멸. 사용자가 읽을 시간은 주되 화면에 눌러앉지는 않게 한다.
  useEffect(() => {
    const timer = setTimeout(() => dismiss(item.id), item.durationMs)
    return () => clearTimeout(timer)
  }, [item.id, item.durationMs, dismiss])

  return (
    <div
      role="status"
      className="pointer-events-auto flex max-w-sm items-start gap-2 rounded-md border px-3 py-2"
      style={{ borderColor: t.color, background: 'var(--color-bg-card)', boxShadow: 'var(--elev-2)' }}
    >
      <span
        className="mt-px inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
        style={{ color: t.color, background: t.bg }}
        aria-hidden
      >
        {t.mark}
      </span>
      <span className="text-[11px] leading-snug text-text-body">{item.message}</span>
      <button
        onClick={() => dismiss(item.id)}
        aria-label="알림 닫기"
        className="ml-auto shrink-0 text-[11px] leading-none text-text-secondary hover:text-text-body"
      >
        ✕
      </button>
    </div>
  )
}

export default function ToastHost() {
  const toasts = useToastStore((s) => s.toasts)
  if (toasts.length === 0) return null
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {toasts.map((t) => (
        <ToastRow key={t.id} item={t} />
      ))}
    </div>
  )
}
